import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { get, put } from '../api';
import { useMe } from '../auth';
import { ACTION_LABEL, Alert, Card, humanize, PageHeader, Spinner, Toggle, useAction, useLoader } from '../components/ui';
import {
  ACTIONS,
  CATEGORY_KEY_RE,
  SEVERITIES,
  type Action,
  type ModerationSettings,
  type Rule,
  type Settings,
  type Severity,
} from '../types';

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));
const newId = () => `rule-${Math.random().toString(36).slice(2, 10)}`;

/** Number input that tolerates transient empty/partial values while typing. */
function NumInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled,
  integer,
  className = '',
  ariaLabel,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  integer?: boolean;
  className?: string;
  ariaLabel?: string;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    if (Number(text) !== value) setText(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      type="number"
      className={`num-input ${className}`}
      value={text}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => {
        setText(e.target.value);
        const n = integer ? parseInt(e.target.value, 10) : parseFloat(e.target.value);
        if (Number.isFinite(n)) onChange(n);
      }}
      onBlur={() => setText(String(value))}
    />
  );
}

function Slider({
  label,
  hint,
  value,
  onChange,
  disabled,
  tone,
}: {
  label: string;
  hint: string;
  value: number;
  onChange: (n: number) => void;
  disabled?: boolean;
  tone: Action;
}) {
  return (
    <div className="slider-field">
      <div className="slider-head">
        <span className={`badge badge-${tone}`}>{label}</span>
        <NumInput value={value} onChange={(n) => onChange(Math.min(1, Math.max(0, n)))} min={0} max={1} step={0.01} disabled={disabled} ariaLabel={`${label} threshold`} />
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`range range-${tone}`}
        aria-label={`${label} threshold`}
      />
      <span className="hint">{hint}</span>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <Card title={title} subtitle={subtitle}>
      {children}
    </Card>
  );
}

function RuleEditor({
  rule,
  categories,
  readOnly,
  onChange,
  onRemove,
  onMove,
  index,
  count,
}: {
  rule: Rule;
  categories: string[];
  readOnly: boolean;
  onChange: (r: Rule) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  index: number;
  count: number;
}) {
  const w = rule.when;
  const setWhen = (changes: Partial<Rule['when']>) => {
    const next: Rule['when'] = { ...w, ...changes };
    for (const k of Object.keys(next) as (keyof Rule['when'])[]) if (next[k] === undefined) delete next[k];
    onChange({ ...rule, when: next });
  };
  const toggleCategory = (c: string) => {
    const set = new Set(w.categories ?? []);
    if (set.has(c)) set.delete(c);
    else set.add(c);
    setWhen({ categories: set.size ? [...set] : undefined });
  };
  const noConditions = Object.values(w).every((v) => v === undefined);
  const fragValue = w.fragmented === undefined ? 'any' : w.fragmented ? 'yes' : 'no';

  return (
    <div className={`rule ${rule.enabled ? '' : 'is-dim'}`}>
      <div className="rule-head">
        <Toggle checked={rule.enabled} disabled={readOnly} onChange={(enabled) => onChange({ ...rule, enabled })} />
        <input
          className="rule-name"
          value={rule.name}
          disabled={readOnly}
          maxLength={120}
          placeholder="Rule name"
          aria-label="Rule name"
          onChange={(e) => onChange({ ...rule, name: e.target.value })}
        />
        {!readOnly && (
          <div className="btn-row">
            <button type="button" className="btn btn-sm btn-ghost" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Move up">
              ↑
            </button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={index === count - 1} onClick={() => onMove(1)} aria-label="Move down">
              ↓
            </button>
            <button type="button" className="btn btn-sm btn-danger-ghost" onClick={onRemove}>
              Remove
            </button>
          </div>
        )}
      </div>

      <div className="rule-body">
        <div className="rule-label">When all of these match</div>
        <div className="cond-grid">
          <label className="field">
            <span>Minimum severity</span>
            <select
              value={w.min_severity ?? ''}
              disabled={readOnly}
              onChange={(e) => setWhen({ min_severity: (e.target.value || undefined) as Severity | undefined })}
            >
              <option value="">Any</option>
              {SEVERITIES.filter((s) => s !== 'none').map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <div className="field">
            <span>
              <label className="check">
                <input
                  type="checkbox"
                  checked={w.min_risk !== undefined}
                  disabled={readOnly}
                  onChange={(e) => setWhen({ min_risk: e.target.checked ? 0.5 : undefined })}
                />
                Minimum risk
              </label>
            </span>
            {w.min_risk !== undefined ? (
              <NumInput value={w.min_risk} min={0} max={1} step={0.05} disabled={readOnly} onChange={(n) => setWhen({ min_risk: Math.min(1, Math.max(0, n)) })} ariaLabel="Minimum risk" />
            ) : (
              <span className="muted small cond-off">any</span>
            )}
          </div>
          <label className="field">
            <span>Fragmented content</span>
            <select
              value={fragValue}
              disabled={readOnly}
              onChange={(e) => setWhen({ fragmented: e.target.value === 'any' ? undefined : e.target.value === 'yes' })}
            >
              <option value="any">Any</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <div className="field">
            <span>
              <label className="check">
                <input
                  type="checkbox"
                  checked={!!w.repeat_offender}
                  disabled={readOnly}
                  onChange={(e) => setWhen({ repeat_offender: e.target.checked ? { count: 3, window_minutes: 60 } : undefined })}
                />
                Repeat offender
              </label>
            </span>
            {w.repeat_offender ? (
              <div className="inline-nums">
                <NumInput
                  integer
                  min={1}
                  value={w.repeat_offender.count}
                  disabled={readOnly}
                  ariaLabel="Violation count"
                  onChange={(n) => setWhen({ repeat_offender: { ...w.repeat_offender!, count: Math.max(1, n) } })}
                />
                <span className="small muted">violations in</span>
                <NumInput
                  integer
                  min={1}
                  value={w.repeat_offender.window_minutes}
                  disabled={readOnly}
                  ariaLabel="Window in minutes"
                  onChange={(n) => setWhen({ repeat_offender: { ...w.repeat_offender!, window_minutes: Math.max(1, n) } })}
                />
                <span className="small muted">min</span>
              </div>
            ) : (
              <span className="muted small cond-off">off</span>
            )}
          </div>
        </div>
        <div className="field">
          <span>Categories {w.categories?.length ? '' : <span className="muted">(any)</span>}</span>
          <div className="chips">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                disabled={readOnly}
                className={`chip ${w.categories?.includes(c) ? 'on' : ''}`}
                aria-pressed={!!w.categories?.includes(c)}
                onClick={() => toggleCategory(c)}
              >
                {humanize(c)}
              </button>
            ))}
          </div>
        </div>
        <div className="rule-then">
          <span className="rule-label">Then at least</span>
          <select value={rule.action} disabled={readOnly} onChange={(e) => onChange({ ...rule, action: e.target.value as Action })}>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {ACTION_LABEL[a]}
              </option>
            ))}
          </select>
        </div>
        {noConditions && <Alert kind="warn">This rule has no conditions and will be ignored. Add at least one condition.</Alert>}
      </div>
    </div>
  );
}

function validate(m: ModerationSettings): string[] {
  const errors: string[] = [];
  const keys = new Set<string>();
  for (const c of m.custom_categories) {
    if (!CATEGORY_KEY_RE.test(c.key)) errors.push(`Custom category "${c.key || '(empty)'}": key must be 2–41 lowercase letters, digits or underscores, starting with a letter`);
    if (keys.has(c.key)) errors.push(`Custom category "${c.key}" is defined twice`);
    keys.add(c.key);
    if (!c.description.trim()) errors.push(`Custom category "${c.key}" needs a description`);
  }
  for (const r of m.rules) {
    if (!r.name.trim()) errors.push('Every rule needs a name');
  }
  const t = m.thresholds;
  if (t.flag > t.block_message || t.block_message > t.block_message_and_user) {
    errors.push('Thresholds should increase: flag ≤ block message ≤ block message + user');
  }
  return errors;
}

export function ModerationSettingsPage() {
  const { isAdmin } = useMe();
  const { data, error, setData } = useLoader(() => get<Settings>('/api/settings'), []);
  const [draft, setDraft] = useState<ModerationSettings | null>(null);
  const [saved, setSaved] = useState(false);
  const act = useAction();
  const readOnly = !isAdmin;

  useEffect(() => {
    if (data) setDraft(clone(data.moderation));
  }, [data]);

  const dirty = useMemo(() => !!data && !!draft && JSON.stringify(draft) !== JSON.stringify(data.moderation), [data, draft]);
  const errors = useMemo(() => (draft ? validate(draft) : []), [draft]);

  if (!data || !draft) return error ? <Alert>{error}</Alert> : <Spinner />;

  const builtin = Object.keys(data.categories).filter((c) => c !== 'none' && !data.moderation.custom_categories.some((x) => x.key === c));
  const allCategories = [...builtin, ...draft.custom_categories.map((c) => c.key).filter((k) => CATEGORY_KEY_RE.test(k))];
  const describe = (c: string) => data.categories[c] ?? draft.custom_categories.find((x) => x.key === c)?.description ?? '';

  const set = (changes: Partial<ModerationSettings>) => {
    setDraft({ ...draft, ...changes });
    setSaved(false);
  };

  const save = async () => {
    if (errors.length) return;
    const res = await act.run('save', () => put<Settings>('/api/settings', { moderation: draft }));
    if (res) {
      setData(res);
      setSaved(true);
    }
  };

  const removeCustom = (i: number) => {
    const key = draft.custom_categories[i].key;
    const category_actions = { ...draft.category_actions };
    delete category_actions[key];
    const rules = draft.rules.map((r) => {
      if (!r.when.categories?.includes(key)) return r;
      const cats = r.when.categories.filter((c) => c !== key);
      const when = { ...r.when, categories: cats.length ? cats : undefined };
      if (!when.categories) delete when.categories;
      return { ...r, when };
    });
    set({ custom_categories: draft.custom_categories.filter((_, j) => j !== i), category_actions, rules });
  };

  const updateRule = (i: number, r: Rule) => set({ rules: draft.rules.map((x, j) => (j === i ? r : x)) });
  const moveRule = (i: number, dir: -1 | 1) => {
    const rules = [...draft.rules];
    [rules[i], rules[i + dir]] = [rules[i + dir], rules[i]];
    set({ rules });
  };

  return (
    <>
      <PageHeader
        title="Moderation rules"
        subtitle="The model scores each message; these settings decide what to do with the scores."
        actions={
          <span className={`jev-pill ${data.jev.mock ? 'is-mock' : ''}`} title={`Model: ${data.jev.model}`}>
            Jev: {data.jev.mock ? 'mock mode' : data.jev.model}
          </span>
        }
      />
      {readOnly && <Alert kind="info">You have read-only access. Ask an admin to change moderation settings.</Alert>}
      {data.jev.mock && (
        <Alert kind="warn">
          The Jev model is running in <strong>mock mode</strong>: decisions are simulated with keyword heuristics, not the real model.
        </Alert>
      )}

      <div className="settings-stack">
        <Section title="Risk thresholds" subtitle="Risk is the highest of the toxic, terms-violation and fragmented probabilities.">
          <div className="sliders">
            <Slider
              label="Flag"
              tone="flag"
              hint="Mark for review; also the minimum risk for categories and flags."
              value={draft.thresholds.flag}
              disabled={readOnly}
              onChange={(flag) => set({ thresholds: { ...draft.thresholds, flag } })}
            />
            <Slider
              label="Block message"
              tone="block_message"
              hint="Recommend hiding the message."
              value={draft.thresholds.block_message}
              disabled={readOnly}
              onChange={(block_message) => set({ thresholds: { ...draft.thresholds, block_message } })}
            />
            <Slider
              label="Block msg + user"
              tone="block_message_and_user"
              hint="Recommend blocking the sender as well."
              value={draft.thresholds.block_message_and_user}
              disabled={readOnly}
              onChange={(block_message_and_user) => set({ thresholds: { ...draft.thresholds, block_message_and_user } })}
            />
          </div>
          <label className="field field-inline">
            <span>Blocking a user by risk alone also requires severity of at least</span>
            <select
              value={draft.block_user_min_severity}
              disabled={readOnly}
              onChange={(e) => set({ block_user_min_severity: e.target.value as Severity })}
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </Section>

        <Section title="Category actions" subtitle="Minimum action when a category is detected with risk at or above the flag threshold.">
          <div className="cat-table">
            {allCategories.map((c) => (
              <div key={c} className="cat-row">
                <div>
                  <div className="strong">{humanize(c)}</div>
                  <div className="small muted">{describe(c)}</div>
                </div>
                <select
                  value={draft.category_actions[c] ?? ''}
                  disabled={readOnly}
                  aria-label={`Action for ${c}`}
                  onChange={(e) => {
                    const category_actions = { ...draft.category_actions };
                    if (e.target.value) category_actions[c] = e.target.value as Action;
                    else delete category_actions[c];
                    set({ category_actions });
                  }}
                >
                  <option value="">None (use thresholds)</option>
                  {ACTIONS.filter((a) => a !== 'allow').map((a) => (
                    <option key={a} value={a}>
                      {ACTION_LABEL[a]}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Custom categories" subtitle="Extra categories specific to your platform, added to the model's classification.">
          {draft.custom_categories.length === 0 && <p className="muted small">No custom categories.</p>}
          <div className="custom-cats">
            {draft.custom_categories.map((c, i) => (
              <div key={i} className="custom-cat">
                <input
                  className={`mono ${c.key && !CATEGORY_KEY_RE.test(c.key) ? 'is-invalid' : ''}`}
                  value={c.key}
                  disabled={readOnly}
                  placeholder="key_name"
                  aria-label="Category key"
                  maxLength={41}
                  onChange={(e) =>
                    set({ custom_categories: draft.custom_categories.map((x, j) => (j === i ? { ...x, key: e.target.value.toLowerCase() } : x)) })
                  }
                />
                <input
                  value={c.description}
                  disabled={readOnly}
                  placeholder="What this category covers"
                  aria-label="Category description"
                  maxLength={500}
                  onChange={(e) =>
                    set({ custom_categories: draft.custom_categories.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })
                  }
                />
                {!readOnly && (
                  <button type="button" className="btn btn-sm btn-danger-ghost" onClick={() => removeCustom(i)}>
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
          {!readOnly && (
            <button
              type="button"
              className="btn btn-sm"
              disabled={draft.custom_categories.length >= 50}
              onClick={() => set({ custom_categories: [...draft.custom_categories, { key: '', description: '' }] })}
            >
              + Add category
            </button>
          )}
        </Section>

        <Section title="Rules" subtitle="Rules only escalate the action, never downgrade it. Evaluated in order after thresholds and category actions.">
          {draft.rules.length === 0 && <p className="muted small">No rules.</p>}
          <div className="rules">
            {draft.rules.map((r, i) => (
              <RuleEditor
                key={r.id}
                rule={r}
                index={i}
                count={draft.rules.length}
                categories={allCategories}
                readOnly={readOnly}
                onChange={(nr) => updateRule(i, nr)}
                onRemove={() => set({ rules: draft.rules.filter((_, j) => j !== i) })}
                onMove={(dir) => moveRule(i, dir)}
              />
            ))}
          </div>
          {!readOnly && (
            <button
              type="button"
              className="btn btn-sm"
              disabled={draft.rules.length >= 50}
              onClick={() =>
                set({
                  rules: [...draft.rules, { id: newId(), name: 'New rule', enabled: true, when: { min_severity: 'high' }, action: 'block_message' }],
                })
              }
            >
              + Add rule
            </button>
          )}
        </Section>

        <Section title="Custom instructions" subtitle="Free-text guidance for the model, on top of your terms & policies.">
          <textarea
            rows={5}
            maxLength={5000}
            value={draft.custom_instructions}
            disabled={readOnly}
            placeholder="e.g. Trading in-game items for real money is not allowed. Mild swearing between friends is fine."
            onChange={(e) => set({ custom_instructions: e.target.value })}
          />
          <span className="hint">{draft.custom_instructions.length} / 5000</span>
        </Section>

        <Section title="Context" subtitle="Recent messages sent to the model with each message, so it can understand conversations.">
          <div className="cond-grid">
            <label className="field">
              <span>Same user's messages</span>
              <NumInput integer min={0} max={50} value={draft.context.same_user_messages} disabled={readOnly}
                onChange={(n) => set({ context: { ...draft.context, same_user_messages: Math.min(50, Math.max(0, n)) } })} />
            </label>
            <label className="field">
              <span>Other users' messages</span>
              <NumInput integer min={0} max={50} value={draft.context.other_user_messages} disabled={readOnly}
                onChange={(n) => set({ context: { ...draft.context, other_user_messages: Math.min(50, Math.max(0, n)) } })} />
            </label>
            <label className="field">
              <span>Window (minutes)</span>
              <NumInput integer min={1} max={10080} value={draft.context.window_minutes} disabled={readOnly}
                onChange={(n) => set({ context: { ...draft.context, window_minutes: Math.min(10080, Math.max(1, n)) } })} />
            </label>
          </div>
        </Section>

        <Section title="Behavior">
          <div className="toggles">
            <Toggle
              checked={draft.fragment_detection}
              disabled={readOnly}
              onChange={(v) => set({ fragment_detection: v })}
              label="Fragment detection"
              hint="Detect harmful content split across several messages from the same user."
            />
            <Toggle
              checked={draft.block_messages_from_blocked_users}
              disabled={readOnly}
              onChange={(v) => set({ block_messages_from_blocked_users: v })}
              label="Block messages from blocked users"
              hint="Messages from users we recommended blocking are always at least 'block message'."
            />
            <Toggle
              checked={draft.cache_repeated_content}
              disabled={readOnly}
              onChange={(v) => set({ cache_repeated_content: v })}
              label="Reuse results for repeated messages"
              hint="Identical long messages (spam waves, templates) reuse an earlier result instead of being evaluated again. Short messages, bursts and direct replies are always evaluated."
            />
          </div>
        </Section>
      </div>

      {!readOnly && (
        <div className="savebar">
          <div className="savebar-msg">
            {errors.length > 0 ? (
              <span className="tone-text-bad small">{errors[0]}</span>
            ) : act.error ? (
              <span className="tone-text-bad small">{act.error}</span>
            ) : saved && !dirty ? (
              <span className="tone-text-ok small">Settings saved</span>
            ) : (
              <span className="small muted">{dirty ? 'You have unsaved changes' : 'No unsaved changes'}</span>
            )}
          </div>
          <div className="btn-row">
            <button className="btn" disabled={!dirty || !!act.busy} onClick={() => { setDraft(clone(data.moderation)); act.setError(null); }}>
              Discard
            </button>
            <button className="btn btn-primary" disabled={!!act.busy || errors.length > 0} onClick={save}>
              {act.busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
